import { Injectable } from '@angular/core';
import { CurrentUserService } from '../../services/current-user.service';
import {
  Product,
  CrossReference,
  CategoryStatistics,
} from '../../types/product';
import {
  CrossReferenceDescription,
  CrossReferenceType,
  CrossOpinionEnum,
  CreatedByOrgTypeEnum,
} from '../../types/types';
import { User } from '../../types/user';

@Injectable({
  providedIn: 'root',
})
export class CrossReferencesManagementService {
  userAccount!: User;

  constructor(private currentUserService: CurrentUserService) {
    currentUserService.aptitudeUser$.subscribe((currentUser) => {
      this.userAccount = currentUser;
    });
  }

  optimisticNoEquivalent(currentProduct: Product): Product {
    const optimisticProduct = { ...currentProduct };

    // Remove Crosses associated with the User's org
    optimisticProduct.crossReferences = currentProduct.crossReferences?.filter(
      (crossReference) => {
        return (
          crossReference.createdByOrg.id !== this.userAccount.organization.id
        );
      },
    );

    optimisticProduct.crossReferences?.push({
      id: 0,
      createdByOrg: this.userAccount.organization,
      createdByOrgType: 'SELLER',
      crossReferenceDescription: CrossReferenceDescription.NO_MATCH,
      crossReferenceType: CrossReferenceType.NO_MATCH,
      marketplaceApproveCount: 0,
      marketplaceDisputeCount: 0,
      notes: [],
      gpoNotes: [],
      toProduct: {
        id: currentProduct.id,
        seller: {} as Product['seller'],
        sysSeller: this.userAccount.organization,
      } as Partial<Product>,
    } as CrossReference);

    return this.setCrossCount(optimisticProduct);
  }

  optimisticPromoteCross(
    currentProduct: Product,
    promotedCross: CrossReference,
  ): Product {
    const optimisticProduct = { ...currentProduct };
    optimisticProduct.crossReferences = optimisticProduct.crossReferences!.map(
      (crossReference) => {
        if (
          crossReference.createdByOrg.id === this.userAccount.organization.id
        ) {
          if (crossReference.id === promotedCross.id) {
            crossReference.crossReferenceType = CrossReferenceType.PRIMARY;
            crossReference.crossReferenceDescription =
              CrossReferenceDescription.SELLER_PRIMARY;
          } else {
            crossReference.crossReferenceType = CrossReferenceType.ALTERNATE;
            crossReference.crossReferenceDescription =
              CrossReferenceDescription.SELLER_ALTERNATE;
          }
        }

        return crossReference;
      },
    );
    return this.setCrossCount(optimisticProduct);
  }

  /**
   * Add Cross
   * @description
   * We do this one slightly different because we require to actually have the crossReference Id for opinion updates.
   */
  optimisticAddCross(
    currentProduct: Product,
    addedCross: CrossReference,
  ): Product {
    const optimisticProduct = { ...currentProduct };

    // Filter out No_Match for user's org
    optimisticProduct.crossReferences =
      optimisticProduct.crossReferences?.filter(
        (crossReference) =>
          !(
            crossReference.createdByOrg.id ===
              this.userAccount.organization.id &&
            crossReference.crossReferenceType === 'NO_MATCH'
          ),
      ) || [];

    const isSeller = this.userAccount.isSeller;
    const orgHasPrimaryCross = optimisticProduct.crossReferences?.some(
      (crossReference) =>
        crossReference.createdByOrg.id === this.userAccount.organization.id &&
        crossReference.crossReferenceType === 'PRIMARY',
    );

    addedCross.crossReferenceType = orgHasPrimaryCross
      ? 'ALTERNATE'
      : 'PRIMARY';
    addedCross.crossReferenceDescription = orgHasPrimaryCross
      ? isSeller
        ? CrossReferenceDescription.SELLER_ALTERNATE
        : CrossReferenceDescription.BUYER_ALTERNATE
      : isSeller
      ? CrossReferenceDescription.SELLER_PRIMARY
      : CrossReferenceDescription.BUYER_PRIMARY;

    addedCross.createdByOrg = {
      id: this.userAccount.organization.id,
      name: this.userAccount.organization.name,
    };

    // handle adding seller's own vizientCross differently
    if (this.userAccount.isSeller && addedCross.isGPOCross) {
      addedCross.createdByOrgType = CreatedByOrgTypeEnum.SELLER;
      const addedVizientCrossIndex =
        optimisticProduct.crossReferences.findIndex(
          (crossReference) => crossReference.id === addedCross.id,
        );
      optimisticProduct.crossReferences[addedVizientCrossIndex] = addedCross;
    } else {
      optimisticProduct.crossReferences.push(addedCross);
    }
    return this.setCrossCount(optimisticProduct);
  }

  optimisticRemoveCross(
    currentProduct: Product,
    removedCross: CrossReference
  ): Product {
    const optimisticProduct = { ...currentProduct };
    optimisticProduct.crossReferences =
      optimisticProduct.crossReferences!.filter((crossReference) => {
        const unmatchedCross =
          crossReference.createdByOrg.id !== this.userAccount.organization.id ||
          crossReference.toProduct.id !== removedCross.toProduct.id;
        return unmatchedCross;
      });

    // If we remove a Primary, promote first alternate if able
    if (removedCross.crossReferenceType === CrossReferenceType.PRIMARY 
        && !this.userAccount.isGPO) {
      const crossToPromote = optimisticProduct.crossReferences.find(
        (crossReference: CrossReference) => {
          return (
            crossReference.createdByOrg.id === this.userAccount.organization.id
          );
        },
      );

      if (crossToPromote) {
        crossToPromote.crossReferenceType = CrossReferenceType.PRIMARY;
        crossToPromote.crossReferenceDescription =
          CrossReferenceDescription.SELLER_PRIMARY;
      }
    }

    // If it is a VizientCross
    if (removedCross.isGPOCross) {
      removedCross.createdByOrgType = CreatedByOrgTypeEnum.GPO;
      removedCross.crossReferenceType = CrossReferenceType.PRIMARY;
      removedCross.crossReferenceDescription =
        CrossReferenceDescription.VIZIENT_CROSS;
      removedCross.createdByOrg = {
        id: -1,
        name: 'Vizient',
      };
      if(!this.userAccount.isGPO)
        optimisticProduct.crossReferences.push(removedCross);
    }

    return this.setCrossCount(optimisticProduct);
  }

  optimisticUpdateCross(
    currentProduct: Product,
    crossReference: CrossReference,
  ) {
    const crossIndex = currentProduct.crossReferences?.findIndex(
      (cross) => cross.id === crossReference.id,
    ) as number;

    currentProduct.crossReferences![crossIndex] = crossReference;

    return this.setCrossCount(currentProduct);
  }

  getCrossCount(crossReferences: CrossReference[]): number {
    return crossReferences?.reduce((count, cross: CrossReference) => {
      return (count += cross.crossReferenceType !== 'NO_MATCH' ? 1 : 0);
    }, 0) as number;
  }

  getCrossVoteTotals(crossReferences: CrossReference[]) {
    return crossReferences.reduce(
      (
        val: { approveCount: number; disputeCount: number },
        crossReference: CrossReference,
      ) => {
        val.approveCount +=
          0 ||
          (crossReference?.opinion?.opinion === CrossOpinionEnum.APPROVE
            ? 1
            : 0);
        val.disputeCount +=
          0 ||
          (crossReference?.opinion?.opinion === CrossOpinionEnum.DISPUTE
            ? 1
            : 0);

        return val;
      },
      { approveCount: 0, disputeCount: 0 },
    );
  }

  private setCrossCount(optimisticProduct: Product): Product {
    const crossCount = this.getCrossCount(
      optimisticProduct.crossReferences || [],
    );

    const crossVoteTotals = this.getCrossVoteTotals(
      optimisticProduct.crossReferences || [],
    );

    // TODO: optimize condition.
    if (optimisticProduct.statistics) {
      Object.assign(optimisticProduct.statistics.crossReferenceStatistic, {
        crossCount,
        ...crossVoteTotals,
      });
    } else {
      optimisticProduct.statistics = {
        crossReferenceStatistic: {
          crossCount,
          ...crossVoteTotals,
        } as CategoryStatistics,
      };
    }
    return optimisticProduct;
  }
}
